import { useCallback, useMemo } from 'react';

import {
  useChatWindowBase,
  type UseChatWindowBaseOptions,
  type UseChatWindowBaseResult,
} from './useChatWindowBase';
import { useGatewayChatLifecycle } from './useGatewayChatLifecycle';
import { useGatewayChatPresentation } from './useGatewayChatPresentation';
import {
  useGatewayPromptPresentation,
  type FileUploadConfigOverride,
  type SuggestedActionsBuilder,
} from './useGatewayPromptPresentation';
import { useGatewayStatusChips } from './useGatewayStatusChips';
import type { GatewayChatLayoutProps } from '../components/GatewayChatLayout';
import type { ConversationSession } from '../types/gateway-chat-types';

export interface UseGatewayChatPageOptions {
  baseOptions?: UseChatWindowBaseOptions;
  titleFallback?: string;
  disclaimerText?: string;
  showProtocolMonitor?: boolean;
  chatWindowWidth?: number;
  autoLoadConversations?: boolean;
  suggestedActionsBuilder?: SuggestedActionsBuilder;
  fileUploadConfigOverride?: FileUploadConfigOverride;
}

export interface UseGatewayChatPageResult {
  layoutProps: GatewayChatLayoutProps;
  chatBase: UseChatWindowBaseResult;
  activeConversation?: ConversationSession;
}

export const useGatewayChatPage = ({
  baseOptions,
  titleFallback = 'Gateway Chat',
  disclaimerText = '',
  showProtocolMonitor = false,
  chatWindowWidth = 680,
  autoLoadConversations = true,
  suggestedActionsBuilder,
  fileUploadConfigOverride,
}: UseGatewayChatPageOptions = {}): UseGatewayChatPageResult => {
  const chatBase = useChatWindowBase(baseOptions);

  const lifecycle = useGatewayChatLifecycle({
    chatBase,
    autoLoadConversations,
  });

  const {
    conversations,
    activeConversation,
    activeConversationId,
    isLoadingConversations,
    isCreatingConversation,
    loadConversations,
    createConversation,
    renameConversation,
    switchConversation,
  } = lifecycle;

  const { chatMessages, chatProgressState } = useGatewayChatPresentation({
    messages: chatBase.stream.messages,
    progressState: chatBase.stream.progressState,
    messagesEndRef: chatBase.messagesEndRef,
  });

  const headerRightContent = useGatewayStatusChips({
    connectionStatus: chatBase.stream.connectionStatus,
    currentRunId: chatBase.stream.currentRunId,
  });

  const { suggestedActions, fileUploadConfig, handleFileDropped, handleSendMessage } =
    useGatewayPromptPresentation({
      prompt: chatBase.prompt,
      isReady: chatBase.isReady,
      activeConversationId,
      suggestedActionsBuilder,
      fileUploadConfigOverride,
      queuePendingFirstMessage: lifecycle.queuePendingFirstMessage,
      clearPendingFirstMessage: lifecycle.clearPendingFirstMessage,
      createConversation,
    });

  const handleRefreshConversations = useCallback(async () => {
    try {
      await loadConversations();
    } catch (error) {
      console.error('Failed to refresh conversations', error);
    }
  }, [loadConversations]);

  const handleCreateConversation = useCallback(async () => {
    try {
      await createConversation();
    } catch (error) {
      console.error('Failed to create conversation', error);
    }
  }, [createConversation]);

  const handleRenameConversation = useCallback(
    async (conversationId: string, name: string) => {
      try {
        await renameConversation(conversationId, name);
      } catch (error) {
        console.error('Failed to rename conversation', error);
      }
    },
    [renameConversation]
  );

  const handleSwitchConversation = useCallback(
    async (conversationId: string) => {
      try {
        await switchConversation(conversationId);
      } catch (error) {
        console.error('Failed to switch conversation', error);
      }
    },
    [switchConversation]
  );

  const layoutProps: GatewayChatLayoutProps = useMemo(
    () => ({
      sessionsPanelProps: {
        conversations,
        activeConversationId,
        isLoadingConversations,
        isCreatingConversation,
        onRefreshConversations: handleRefreshConversations,
        onCreateConversation: handleCreateConversation,
        onSelectConversation: handleSwitchConversation,
        onRenameConversation: handleRenameConversation,
      },
      chatWindowProps: {
        width: chatWindowWidth,
        title: activeConversation?.conversation_name ?? titleFallback,
        headerRightContent,
        messages: chatMessages,
        isWaitingForResponse: chatBase.isWaitingForResponse,
        isReady: chatBase.isReady,
        progressState: chatProgressState,
        userPrompt: chatBase.prompt.userPrompt,
        onUserPromptChange: chatBase.prompt.onUserPromptChange,
        onSendMessage: handleSendMessage,
        isOwner: chatBase.isOwner,
        userInitials: chatBase.userInitials,
        suggestedActions,
        disclaimerText,
        chatWindowRef: chatBase.chatWindowRef,
        messagesEndRef: chatBase.messagesEndRef,
        fileUploadConfig,
      },
      protocolMonitorProps: showProtocolMonitor
        ? { events: chatBase.stream.recentEvents }
        : undefined,
      showProtocolMonitor,
      enableDragDrop: true,
      onFileDropped: handleFileDropped,
      allowedFileTypes: ['.csv'],
      maxFileSizeMB: 10,
    }),
    [
      activeConversation,
      activeConversationId,
      chatBase.chatWindowRef,
      chatBase.isOwner,
      chatBase.isReady,
      chatBase.isWaitingForResponse,
      chatBase.messagesEndRef,
      chatBase.prompt.onUserPromptChange,
      chatBase.prompt.userPrompt,
      chatBase.stream.recentEvents,
      chatBase.userInitials,
      chatMessages,
      chatProgressState,
      chatWindowWidth,
      conversations,
      disclaimerText,
      fileUploadConfig,
      handleCreateConversation,
      handleFileDropped,
      handleRefreshConversations,
      handleRenameConversation,
      handleSendMessage,
      handleSwitchConversation,
      headerRightContent,
      isCreatingConversation,
      isLoadingConversations,
      showProtocolMonitor,
      suggestedActions,
      titleFallback,
    ]
  );

  return {
    layoutProps,
    chatBase,
    activeConversation,
  };
};
